import React, { MouseEvent, ReactNode } from 'react';
import ListQueryCreatorComponent from './detail/ListQueryCreatorComponent';
import { List } from '../../domain/list/ListDomains';
import ListAccessComponent from './detail/ListAccessComponent';
import ListVariableComponent from './detail/ListVariableComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../constant/CollectionNames';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { RootState } from '../../redux/store';
import { ComponentState, enterComponent, leaveComponent } from '../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ComponentName from '../../constant/ComponentName';
import Breadcrumb from '../Breadcrumb';
import { ListState } from '../../redux/reducers/ListReducer';
import { changeList, changeListData, changeListDependencies, changeListDirty, changeListIsUpdate, clearList } from '../../redux/actions/ListActions';
import CustomTabv2 from '../custom/CustomTabv2';
import CustomEditorItemComponent from '../custom/CustomEditorItemComponent';
import { isStringValid } from '../../util/Generator';
import MessageUtil from '../../util/MessageUtil';
import LanguageUtil from '../../util/LanguageUtil';
import ListService from '../../service/ListService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import RoleHelper from '../../RoleHelper';
import ComponentAuditLog from '../ComponentAuditLog';
import CustomLoading from '../custom/CustomLoading';
import ValueFactory from '../../util/ValueFactory';
import { clearPage } from '../../redux/actions/PageActions';
import ListQueryDataViewComponent from './detail/ListQueryDataViewComponent';
import { ComponentDependency } from '../../domain/ui/ComponentMetadata';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../helpers/StandardEditorContainerComponent';
import eq = query.eq;
import { ConfirmPopup } from 'primereact/confirmpopup';
import { AuditLog } from '../../domain/rbac/RbacDomains';
import ListDataService from '../../service/ListDataService';
import { prepareListPreview, ListHistoryMeta } from '../../helpers/ListHistoryPreviewHelper';

class ListQueryEditorContainer extends StandardEditorContainerComponent<Props, State, List> {
  public state: State = new State();

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly listService: ListService = ServiceContainer.getInstance().get(ServiceNames.LIST);
  private readonly listDataService: ListDataService = ServiceContainer.getInstance().get(ServiceNames.LIST_DATA);

  private listSubscription: subscribe.Subscription | undefined;

  private dependencySubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.LIST,
      icon: 'list',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      list: { list },
      isUpdate,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={list.name}
          description={list.description ? list.description : ''}
          status={list?.stage || 'DRAFT'}
          component={ComponentName.LIST}
          isUpdate={isUpdate}
          icon={'sidebar-list'}
          onChangeName={this.onChangeListName}
          onChangeDescription={this.onChangeListDescription}
          onSave={this.saveList}
          onDeploy={this.deployList}
          onUndeploy={this.undeployList}
          onRemove={this.removeList}
          disableSave={!RoleHelper.canEdit('QueryList')}
          disableRemove={!RoleHelper.canEdit('QueryList')}
          disableNameChange={!RoleHelper.canEdit('QueryList')}
          disableDescriptionChange={!RoleHelper.canEdit('QueryList')}
          disableDeploy={!RoleHelper.canDeploy('QueryList')}
          disableUndeploy={!RoleHelper.canDeploy('QueryList')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full  bg-white  rounded '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearList } = this.props;
    clearList();
    await super.componentWillUnmount();
  }

  protected _startTracker = async () => {
    const listId = this.getListFromRoute();
    if (listId == undefined) {
      this.setState({ loading: false });
    }
    const listQuery = query.QueryBuilder.select().from(CollectionNames.LISTS).where(eq('id', listId)).build();
    this.listSubscription = subscribe.Formica.subscribe(CollectionNames.LISTS, listQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.listSubscription]);
    if (listId != undefined) {
      const dependencyQuery = query.QueryBuilder.select().from(CollectionNames.COMPONENT_DEPENDENCY).where(eq('meta.list', listId)).build();
      this.dependencySubscription = subscribe.Formica.subscribe(CollectionNames.COMPONENT_DEPENDENCY, dependencyQuery);
      this.computation.addSubscription(this.dependencySubscription);
    }
  };

  protected _stopTracker = async () => {
    await this.listSubscription?.stop();
    await this.dependencySubscription?.stop();
    this.computation?.stop();
  };

  protected tracker = async () => {
    const { changeList, changeListDependencies, changeListDirty } = this.props;
    if (this.listSubscription?.ready()) {
      const listId = this.getListFromRoute();
      if (listId == undefined) {
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        changeListDirty(false);
      }
      const listQuery = query.QueryBuilder.select().from(CollectionNames.LISTS).where(eq('id', listId)).build();
      const dependencyQuery = query.QueryBuilder.select().from(CollectionNames.COMPONENT_DEPENDENCY).where(eq('meta.list', listId)).build();
      const list: List = (await this.repository.find(listQuery)) || ValueFactory.initializeList();
      const dependencies = await this.repository.findMany<ComponentDependency>(dependencyQuery);
      changeList(list || null);
      changeListDependencies(dependencies);
      if (list) {
        RoleHelper.setCurrentAccess(list);
      }
      changeListDirty(false);
      this.setState({ loading: false });
    }
  };

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private async removeAll() {
    const {
      list: { list },
      changeList,
      component: { selectedComponentIds },
    } = this.props;
    if (selectedComponentIds.length > 0) {
      selectedComponentIds.forEach((variable: any) => {
        if (!variable.key) {
          list.fields = list.fields.filter((field) => field != variable);
          changeList(list);
        } else {
          MessageUtil.warning({
            summary: LanguageUtil.get({ id: 'common.label.key' }),
            detail: LanguageUtil.get({ id: 'messages.warning.selectedKey', defaultMessage: 'The variable selected as the key cannot be deleted!' }),
          });
        }
      });
    }
  }

  private getListFromRoute = () => {
    const { changeListIsUpdate } = this.props;
    const id = this.props.router.params.id;
    changeListIsUpdate(id != undefined);
    return id;
  };

  protected getTabs = () => {
    const {
      list: { list },
    } = this.props;
    return [
      {
        key: 'Schema',
        label: LanguageUtil.get({ id: 'common.tabs.schema', defaultMessage: 'Schema' }),
        content: <ListVariableComponent type={'EXTERNAL_LIST'} />,
        testid: `${ComponentName.LIST}_schema_tab`,
      },
      {
        key: 'List',
        label: LanguageUtil.get({ id: 'common.tabs.list', defaultMessage: 'List' }),
        content: <ListQueryDataViewComponent historyMode={!!this.preview} historyMeta={this.state.historyMeta} />,
        disable: list.stage == 'DRAFT',
        testid: `${ComponentName.LIST}_list_tab`,
      },
      {
        key: 'Query',
        label: LanguageUtil.get({ id: 'common.tabs.Query', defaultMessage: 'Query' }),
        content: <ListQueryCreatorComponent />,
        testid: `${ComponentName.LIST}_query_tab`,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <ListAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.LIST, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.LIST} documentName={this.props.list.list.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.LIST, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  private onChangeListName = (name: string) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    const value = name.trim();
    if (isStringValid(value)) {
      list.name = value;
      changeList(list);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeListDescription = (description: string) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    list.description = description;
    changeList(list);
  };

  private saveList = async () => {
    const {
      list: { list, isUpdate },
      changeListIsUpdate,
    } = this.props;
    try {
      list.type = 'EXTERNAL_LIST';
      const result = await this.listService.saveQueryList(list, isUpdate);
      if (result) {
        changeListIsUpdate(true);
        if (!isUpdate) this.switchToUpdate();
      }
    } catch (e) {
      console.error(e);
    }
  };

  private switchToUpdate = () => {
    const {
      list: { list },
      enterComponent,
    } = this.props;
    if (list.name == '') return;
    const {
      router: { navigate, params },
    } = this.props;
    enterComponent({ component: ComponentName.LIST, id: list.name });
    navigate(`/list${params.folderId ? `/folder/${params.folderId}` : ''}/query/edit/${list.name}`, { replace: true });
    this.resubscribeTracker();
  };

  private deployList = (e: MouseEvent<HTMLElement>) => {
    const {
      list: { list },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.listService.saveQueryList(list, true);
        await this.listService.deployList(list);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deployList', defaultMessage: 'Are you sure to deploy this list?' }),
    });
  };

  private undeployList = (e: MouseEvent<HTMLElement>) => {
    const {
      list: { list },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => confirmUndeployWithImpact(ComponentName.LIST, [list.name], () => this.listService.undeployList(list)),
      message: LanguageUtil.get({ id: 'messages.confirm.undeployList', defaultMessage: 'Are you sure to undeploy this list?' }),
    });
  };

  private removeList = async (e: MouseEvent<HTMLElement>) => {
    const {
      list: { list },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.listService.removelist(list);
        if (success) {
          navigate('/list');
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeList', defaultMessage: 'Are you sure to remove this list?' }),
    });
  };

  protected changeItem = (item: List) => {
    const { changeList } = this.props;
    changeList(item);
  };

  // See ListHistoryPreviewHelper.prepareListPreview for the full rationale (shared with
  // ListFileEditorContainer).
  protected async _preparePreview(log: AuditLog): Promise<void> {
    const { changeListData } = this.props;
    const { list, historyItems, historyMeta } = await prepareListPreview(log, this.listService, this.listDataService);
    this.changeItem(list);
    changeListData(historyItems);
    this.setState({ historyMeta, loading: false });
  }
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
  historyMeta?: ListHistoryMeta;
}

const mapStateToProps = (state: RootState) => ({
  list: state.list as ListState,
  isUpdate: state.list.isUpdate as boolean,
  component: state.component as ComponentState,
});
const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changeList,
  changeListIsUpdate,
  changeListData,
  clearList,
  changeListDependencies,
  clearPage,
  changeListDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ListQueryEditorContainer));
